import { Modal } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";
import FormDigitalCertificate from "../form/FormDigitalCertificate";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  saveDigitalCertificate,
  getPartnersById,
} from "@/common/services/partners.api";
import { IPartner } from "@/types/partners.type";
import ModalOverwriteConfirmation from "./ModalOverwriteConfirmation";
export interface CertificateApiResponse {
  ok: boolean;
  message?: string;
  code?: "CERTIFICATE_EXISTS";
  existingFileName?: string;
}

type SaveCertificateData = {
  partnersId: string;
  certificate: string;
  password_certificate: string;
  forceOverwrite?: boolean;
};

type Props = {
  partnersId: string;
  onClose: () => void;
};

export default function ModalDigitalCertificate({
  partnersId,
  onClose,
}: Props) {
  const { showToast } = useToastContext();
  const [, setPartnerData] = useState<IPartner | null>(null);
  // const [isLoading, setIsLoading] = useState(true);
  const [showOverwriteConfirmation, setShowOverwriteConfirmation] =
    useState(false);
  const [pendingUploadData, setPendingUploadData] =
    useState<SaveCertificateData | null>(null);

  const notifySuccess = useCallback(
    (text: string) => showToast({ text, type: "success" }),
    [showToast]
  );

  const notifyError = useCallback(
    (text: string) => showToast({ text, type: "error" }),
    [showToast]
  );

  useEffect(() => {
    async function loadInitialData() {
      try {
        const response = await getPartnersById(partnersId);
        if (response.ok) {
          setPartnerData(response.data as IPartner);
        } else {
          notifyError("Não foi possível carregar os dados do parceiro.");
          onClose();
        }
      } catch (err) {
        notifyError("Erro de comunicação ao carregar dados iniciais.");
        onClose();
      }
    }
    loadInitialData();
  }, [partnersId, onClose, notifyError]);

  const handleSubmission = async (
    values: SaveCertificateData,
    forceOverwrite: boolean = false
  ) => {
    const submissionData = { ...values, forceOverwrite };

    try {
      const response = (await saveDigitalCertificate(
        submissionData
      )) as CertificateApiResponse;

      if (
        !response.ok &&
        (response as CertificateApiResponse).code === "CERTIFICATE_EXISTS"
      ) {
        setPendingUploadData(values);
        setShowOverwriteConfirmation(true);
        return { ok: false, code: "CERTIFICATE_EXISTS" };
      }

      if (response.ok) {
        notifySuccess(`Certificado salvo com sucesso!`);
        onClose();
        return { ok: true };
      }
      return {
        ok: false,
        errors: [{ message: response.message || "Erro desconhecido" }],
      };
    } catch (err) {
      console.error(err);
      notifyError("Erro na comunicação com o servidor.");
      return { ok: false, errors: [{ message: "Erro de rede/servidor" }] };
    }
  };

  const handleConfirmOverwrite = () => {
    if (pendingUploadData) {
      handleSubmission(pendingUploadData, true);
    }
    setShowOverwriteConfirmation(false);
    setPendingUploadData(null);
  };

  const handleCancelOverwrite = () => {
    setShowOverwriteConfirmation(false);
    setPendingUploadData(null);
  };

  return (
    <Modal size={"2xl"} className="relative" show={true} onClose={onClose}>
      <Modal.Header className="text-black">
        <div className="flex items-center justify-start gap-3 text-black">
          <span>Upload de Certificado Digital</span>
        </div>
      </Modal.Header>
      <Modal.Body className="w-full relative border-gray flex flex-col text-black">
        <FormDigitalCertificate
          partnersId={partnersId}
          onSubmit={(values) => handleSubmission(values, false)}
        />

        {showOverwriteConfirmation && (
          <ModalOverwriteConfirmation
            onConfirm={handleConfirmOverwrite}
            onCancel={handleCancelOverwrite}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
